'use client'

import { useState, useEffect, useMemo } from 'react'
import { AlertCircle, Eye, Filter, RefreshCw, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { ProcessedTheme, BrowserRow } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../../ProcessedEntry/DomainEntry/processedTypes'
import type { CttCampaign, RawEntry, SelectedRow } from '../trustCenterTypes'
import { TrustCenterProcessedCard } from '../TrustCenterProcessedCard'
import { TrustCenterBrandCard } from '../TrustCenterBrandCard'
import { CttDetailPanel } from './CttDetailPanel'
import { CttProcessedEntryDetail } from './processed_entry/CttProcessedEntryDetail'
import { CttBrandCampaignDetail } from './brand/CttBrandCampaignDetail'

function CampStat({ label, value, tone, isDark = false }: { label: string; value: string; tone: 'sky' | 'amber'; isDark?: boolean }) {
  const bg = 'bg-card'
  const lbl = 'text-subtle'
  const val = tone === 'sky' ? 'text-signal' : 'text-warning'
  return (
    <div className={`px-4 py-3 ${bg}`}>
      <div className={`font-mono text-[9px] uppercase tracking-widest ${lbl}`}>{label}</div>
      <div className={`font-mono text-[22px] leading-none tracking-tight mt-1 ${val}`}>{value}</div>
    </div>
  )
}

export function CttTrustCenterView({ theme = 'dark' }: { theme?: ProcessedTheme }) {
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const panelBg = 'bg-card'
  const border  = 'border-border'
  const gapBg   = 'bg-muted'
  const softBg  = 'bg-secondary'
  const label   = 'text-muted-foreground'
  const muted   = 'text-muted-foreground'
  const text    = 'text-foreground'

  const [brandCampaigns, setBrandCampaigns] = useState<CttCampaign[]>([])
  const [processedEntries, setProcessedEntries] = useState<RawEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subTab, setSubTab] = useState<'all' | 'brand' | 'processed'>('all')
  const [query, setQuery] = useState('')
  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null)
  const [detailEntry, setDetailEntry] = useState<RawEntry | null>(null)
  const [detailBrand, setDetailBrand] = useState<CttCampaign | null>(null)
  const [browserRows, setBrowserRows] = useState<BrowserRow[]>([])
  const [browserRowsLoading, setBrowserRowsLoading] = useState(false)

  const fetchEntries = () => {
    setLoading(true)
    Promise.all([
      fetch('/api/campaigns/ctt').then((r) => r.json()),
      fetch('/api/campaigns/ctt/unified').then((r) => r.json()),
    ])
      .then(([brandPayload, unifiedPayload]) => {
        if (brandPayload?.success) setBrandCampaigns(brandPayload.data.entries ?? [])
        else setError(brandPayload?.error ?? 'Failed to load brand campaigns')
        if (unifiedPayload?.success) setProcessedEntries(unifiedPayload.data.entries ?? [])
        else setError(unifiedPayload?.error ?? 'Failed to load unified entries')
      })
      .catch(() => setError('Network error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchEntries() }, [])

  // Load browser data whenever selectedRow changes
  useEffect(() => {
    setBrowserRows([])
    setBrowserRowsLoading(false)
    if (!selectedRow) return
    const domainId = selectedRow.data.user_entry_domain_id
    if (!domainId) return
    setBrowserRowsLoading(true)
    fetch(`/api/campaigns/ctt/entry-domains?userEntryDomainId=${domainId}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          setBrowserRows(
            (res.data?.browserData ?? []).filter(
              (r: BrowserRow) => r.entryDomainId === domainId && r.r2Key,
            ),
          )
        }
      })
      .catch(() => null)
      .finally(() => setBrowserRowsLoading(false))
  }, [selectedRow])

  const allEntries = useMemo(() => {
    type AllRow = { kind: 'brand'; data: CttCampaign } | { kind: 'processed'; data: RawEntry }
    const rows: AllRow[] = [
      ...brandCampaigns.map((d) => ({ kind: 'brand' as const, data: d })),
      ...processedEntries.map((d) => ({ kind: 'processed' as const, data: d })),
    ]
    return rows.sort((a, b) => new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime())
  }, [brandCampaigns, processedEntries])

  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    if (subTab === 'all') {
      if (!q) return allEntries
      return allEntries.filter(({ kind, data }) =>
        kind === 'brand'
          ? (data as CttCampaign).headline.toLowerCase().includes(q) || (data as CttCampaign).description.toLowerCase().includes(q)
          : ((data as RawEntry).headline ?? '').toLowerCase().includes(q) || ((data as RawEntry).message_content ?? '').toLowerCase().includes(q) || ((data as RawEntry).sender ?? '').toLowerCase().includes(q),
      )
    }
    if (subTab === 'brand') {
      return q ? brandCampaigns.filter((e) => e.headline.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)) : brandCampaigns
    }
    return q ? processedEntries.filter((e) => (e.headline ?? '').toLowerCase().includes(q) || (e.message_content ?? '').toLowerCase().includes(q) || (e.sender ?? '').toLowerCase().includes(q)) : processedEntries
  }, [subTab, query, allEntries, brandCampaigns, processedEntries])

  const selectRow = (row: SelectedRow) => {
    setSelectedRow((prev) => prev?.data.id === row.data.id ? null : row)
  }

  if (detailEntry) {
    return (
      <CttProcessedEntryDetail
        entry={detailEntry}
        theme={theme}
        onBack={() => setDetailEntry(null)}
        onPromoted={fetchEntries}
        recentCampaigns={brandCampaigns}
      />
    )
  }

  if (detailBrand) {
    return (
      <CttBrandCampaignDetail
        entry={detailBrand}
        theme={theme}
        onBack={() => setDetailBrand(null)}
        onSaved={fetchEntries}
      />
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${muted}`}>Loading…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center gap-3 text-destructive">
        <AlertCircle className="h-5 w-5" />
        <span className="font-mono text-[13px]">{error}</span>
      </div>
    )
  }

  return (
    <div className="flex h-screen min-h-0">
      {/* ── Left: list ── */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        <div className={`border-b px-6 py-4 shrink-0 ${border} ${panelBg}`}>
          <div className={`font-mono text-[10px] uppercase tracking-widest ${label}`}>// CTT · Trust Center</div>
          <h1 className={`text-xl font-semibold tracking-tight mt-0.5 ${text}`}>Trust Center</h1>
          <p className={`text-[13px] mt-0.5 ${label}`}>
            Unified view from <span className="font-mono text-[12px]">brand_campaigns_unified</span>. Promote processed entries into brand campaigns.
          </p>
          <div className={`grid grid-cols-2 gap-px mt-4 border ${gapBg} ${border}`}>
            <CampStat label="Brand campaigns" value={String(brandCampaigns.length).padStart(2, '0')} tone="sky" isDark={isDark} />
            <CampStat label="Processed entries" value={String(processedEntries.length).padStart(2, '0')} tone="amber" isDark={isDark} />
          </div>
        </div>

        <div className={`flex border-b shrink-0 ${border} ${softBg}`}>
          {([
            ['all', `All (${allEntries.length})`],
            ['brand', `Brand campaigns (${brandCampaigns.length})`],
            ['processed', `Processed entries (${processedEntries.length})`],
          ] as const).map(([id, tabLabel]) => (
            <button
              key={id}
              onClick={() => { setSubTab(id); setQuery(''); setSelectedRow(null); setDetailEntry(null) }}
              className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors border-b-2 ${subTab === id ? `border-signal text-signal ${panelBg}` : `border-transparent ${muted} hover:${text} hover:bg-white/5`}`}
            >
              {tabLabel}
            </button>
          ))}
        </div>

        <div className={`px-4 py-3 border-b flex items-center gap-2 shrink-0 ${border} ${panelBg}`}>
          <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
          <Input
            placeholder="Filter by title, sender, message…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
          />
          <Filter className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
          <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
            {filtered.length} / {subTab === 'all' ? allEntries.length : subTab === 'brand' ? brandCampaigns.length : processedEntries.length}
          </span>
          <button onClick={fetchEntries} title="Refresh" className={`ml-1 flex items-center justify-center h-6 w-6 border transition-colors hover:text-signal hover:border-signal ${border} ${muted}`}>
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          {filtered.length === 0 ? (
            <p className={`px-7 py-14 text-center font-mono text-[11px] uppercase tracking-widest text-subtle`}>No entries</p>
          ) : (
            <div className={`border mx-5 my-4 overflow-hidden border-border bg-card`}>
              {subTab === 'all'
                ? (filtered as { kind: 'brand' | 'processed'; data: CttCampaign | RawEntry }[]).map(({ kind, data }) =>
                    kind === 'brand'
                      ? <TrustCenterBrandCard key={data.id} entry={data as CttCampaign} theme={theme} isSelected={false} onClick={() => setDetailBrand(data as CttCampaign)} />
                      : <TrustCenterProcessedCard key={data.id} entry={data as RawEntry} theme={theme} isSelected={false} onClick={() => setDetailEntry(data as RawEntry)} />
                  )
                : subTab === 'brand'
                ? (filtered as CttCampaign[]).map((e) => (
                    <TrustCenterBrandCard key={e.id} entry={e} theme={theme} isSelected={false} onClick={() => setDetailBrand(e)} />
                  ))
                : (filtered as RawEntry[]).map((e) => (
                    <TrustCenterProcessedCard key={e.id} entry={e} theme={theme} isSelected={false} onClick={() => setDetailEntry(e)} />
                  ))
              }
            </div>
          )}
        </div>
      </div>

      {/* ── Right: detail panel ── */}
      <div className={`w-[420px] shrink-0 border-l flex flex-col ${border} ${panelBg}`}>
        {!selectedRow ? (
          <div className={`flex flex-1 flex-col items-center justify-center gap-3 text-subtle`}>
            <Eye className="h-10 w-10" />
            <p className="font-mono text-[10px] uppercase tracking-widest">Select an entry to preview</p>
          </div>
        ) : (
          <CttDetailPanel
            selectedRow={selectedRow}
            theme={theme}
            browserRows={browserRows}
            browserRowsLoading={browserRowsLoading}
            onClose={() => setSelectedRow(null)}
            onSaved={fetchEntries}
          />
        )}
      </div>
    </div>
  )
}
